export type SdkLogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface SdkLogEvent {
    readonly domain: string
    readonly level: SdkLogLevel
    readonly message: string
    readonly data?: unknown
    readonly timestamp: number
}

export interface SdkObservers {
    onEvent?(event: SdkLogEvent): void | Promise<void>
    onMetric?(name: string, value: number, tags?: Record<string, string>): void | Promise<void>
}

export type SdkObserver = SdkObservers

function runObserverHookSafely(run: () => void | Promise<void>): void {
    try {
        const result = run()
        if (result && typeof (result as PromiseLike<void>).then === 'function') {
            void Promise.resolve(result).catch(() => {})
        }
    } catch {
        // Observers are best-effort only.
    }
}

export function createNoopObservers(): SdkObservers {
    return {
        onEvent() {},
        onMetric() {},
    }
}

export function emitSdkEvent(
    observers: SdkObservers | undefined,
    event: Omit<SdkLogEvent, 'timestamp'> & { timestamp?: number },
): void {
    if (!observers?.onEvent) {
        return
    }

    runObserverHookSafely(() => observers.onEvent?.({
        ...event,
        timestamp: event.timestamp ?? Date.now(),
    }))
}

export function emitSdkMetric(
    observers: SdkObservers | undefined,
    name: string,
    value: number,
    tags?: Record<string, string>,
): void {
    if (!observers?.onMetric) {
        return
    }

    runObserverHookSafely(() => observers.onMetric?.(name, value, tags))
}
